// src/pages/legal/LegalPages.jsx
// Updated: full i18n via useTranslation + cookie prefs saved to backend
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../i18n";
import axios from "axios";
import { updateConsent, updateAdsConsent } from "../analytics";  // ← زيد هاد السطر
const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";

/* ── shared CSS injected once ───────────────────────────────────────────────── */
let _legalCSSInjected = false;
function LegalStyles() {
  useEffect(() => {
    if (_legalCSSInjected || document.getElementById("legal-css")) return;
    _legalCSSInjected = true;
    const s = document.createElement("style");
    s.id = "legal-css";
    s.textContent = LEGAL_CSS;
    document.head.appendChild(s);
  }, []);
  return null;
}

/* ── back-to-top hook ────────────────────────────────────────────────────────── */
function useScrollTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return show;
}

/* ── reading progress bar ────────────────────────────────────────────────────── */
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const h   = el.scrollHeight - el.clientHeight;
      setPct(h > 0 ? Math.min(100, (top / h) * 100) : 0);
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <div className="lp-progress-track" aria-hidden>
      <div className="lp-progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ── TOC builder ─────────────────────────────────────────────────────────────── */
function TableOfContents({ sections }) {
  const [active, setActive] = useState(null);
  const { t, lang } = useTranslation();
  const isRTL = lang === "ar";

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }); },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className="lp-toc" aria-label={t("toc_label") || "Table of contents"} dir={isRTL ? "rtl" : "ltr"}>
      <div className="lp-toc__head">
        <span className="lp-toc__bar" />
        <span className="lp-toc__title">{t("toc_title") || "Contents"}</span>
      </div>
      <ol className="lp-toc__list">
        {sections.map((s, i) => (
          <li key={s.id}>
            <button
              className={`lp-toc__item${active === s.id ? " lp-toc__item--on" : ""}`}
              onClick={() => scrollTo(s.id)}
            >
              <span className="lp-toc__num">{String(i + 1).padStart(2, "0")}</span>
              <span>{s.label}</span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ── shared page shell ───────────────────────────────────────────────────────── */
function LegalShell({ icon, badge, title, subtitle, updated, sections, children }) {
  const showTop = useScrollTop();
  const { t, lang } = useTranslation();
  const isRTL = lang === "ar";

  return (
    <div className="lp-root" dir={isRTL ? "rtl" : "ltr"}>
      <LegalStyles />
      <ReadingProgress />

      <header className="lp-hero">
        <div className="lp-hero__bg" aria-hidden />
        <div className="lp-hero__noise" aria-hidden />
        <div className="lp-container lp-hero__inner">
          <div className="lp-hero__icon">{icon}</div>
          <div>
            <span className="lp-hero__badge">{badge}</span>
            <h1 className="lp-hero__title">{title}</h1>
            {subtitle && <p className="lp-hero__sub">{subtitle}</p>}
            {updated && (
              <div className="lp-hero__meta">
                <i className="bi bi-calendar3" aria-hidden />
                {t("last_updated") || "Last updated"}: <time>{updated}</time>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="lp-breadcrumb">
        <div className="lp-container lp-breadcrumb__inner">
          <Link to="/" className="lp-breadcrumb__link">
            <i className="bi bi-house" /> {t("home") || "Home"}
          </Link>
          <span className="lp-breadcrumb__sep" aria-hidden>/</span>
          <span className="lp-breadcrumb__cur">{title}</span>
        </div>
      </div>

      <div className="lp-container lp-body">
        {sections && <TableOfContents sections={sections} />}
        <article className="lp-article">{children}</article>
      </div>

      <button
        className={`lp-btt${showTop ? " lp-btt--on" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label={t("back_to_top") || "Back to top"}
      >
        <i className="bi bi-arrow-up" />
      </button>
    </div>
  );
}

/* ── section component ───────────────────────────────────────────────────────── */
function Section({ id, number, title, children }) {
  return (
    <section id={id} className="lp-section">
      <div className="lp-section__head">
        <span className="lp-section__num">{number}</span>
        <h2 className="lp-section__title">{title}</h2>
      </div>
      <div className="lp-section__body">{children}</div>
    </section>
  );
}

function P({ children }) { return <p className="lp-p">{children}</p>; }

function Ul({ items }) {
  return (
    <ul className="lp-ul">
      {items.map((it, i) => (
        <li key={i} className="lp-li">
          <span className="lp-li__dot" aria-hidden />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function InfoBox({ icon = "bi-info-circle", children }) {
  return (
    <div className="lp-info">
      <i className={`bi ${icon} lp-info__icon`} aria-hidden />
      <div>{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PRIVACY PAGE
═══════════════════════════════════════════════════════════════════════════════ */
export function PrivacyPage() {
  const { t } = useTranslation();

  const sections = [
    { id: "collect",  label: t("priv_s1_title") || "Information We Collect" },
    { id: "use",      label: t("priv_s2_title") || "How We Use It" },
    { id: "share",    label: t("priv_s3_title") || "Sharing & Disclosure" },
    { id: "cookies",  label: t("priv_s4_title") || "Cookies & Tracking" },
    { id: "rights",   label: t("priv_s5_title") || "Your Rights" },
    { id: "security", label: t("priv_s6_title") || "Security" },
    { id: "contact",  label: t("priv_s7_title") || "Contact Us" },
  ];

  return (
    <LegalShell
      icon={<i className="bi bi-shield-lock" />}
      badge={t("legal_badge") || "Legal Document"}
      title={t("privacy_title") || "Privacy Policy"}
      subtitle={t("privacy_subtitle") || "We respect your privacy and are committed to protecting your personal data."}
      updated={t("privacy_updated") || "March 15, 2026"}
      sections={sections}
    >
      <InfoBox icon="bi-shield-check">
        {t("privacy_intro") || <>This policy explains how <strong>24News</strong> collects, uses, and protects your personal information when you use our services.</>}
      </InfoBox>

      <Section id="collect" number="01" title={t("priv_s1_title") || "Information We Collect"}>
        <P>{t("priv_s1_p1") || "We collect information you provide directly to us and data generated automatically when you use our platform."}</P>
        <Ul items={[
          t("priv_s1_li1") || "Account information: name, email address, username, and password",
          t("priv_s1_li2") || "Profile information: avatar, bio, and language preferences",
          t("priv_s1_li3") || "Content you create: comments, reactions, and saved articles",
          t("priv_s1_li4") || "Usage data: pages visited, time spent, and features used",
          t("priv_s1_li5") || "Device information: IP address, browser type, and operating system",
          t("priv_s1_li6") || "Location data: approximate geographic location based on IP",
        ]} />
      </Section>

      <Section id="use" number="02" title={t("priv_s2_title") || "How We Use Your Information"}>
        <P>{t("priv_s2_p1") || "Your information helps us deliver, improve, and personalize our news experience."}</P>
        <Ul items={[
          t("priv_s2_li1") || "Provide and maintain our services",
          t("priv_s2_li2") || "Personalize your news feed and recommendations",
          t("priv_s2_li3") || "Send you important updates and notifications",
          t("priv_s2_li4") || "Analyze usage patterns to improve our platform",
          t("priv_s2_li5") || "Prevent fraud, abuse, and security threats",
          t("priv_s2_li6") || "Comply with legal obligations",
        ]} />
      </Section>

      <Section id="share" number="03" title={t("priv_s3_title") || "Sharing & Disclosure"}>
        <P>{t("priv_s3_p1") || "We do not sell your personal data. We may share information only in limited circumstances."}</P>
        <Ul items={[
          t("priv_s3_li1") || "With service providers who assist in operating our platform",
          t("priv_s3_li2") || "When required by law or valid legal process",
          t("priv_s3_li3") || "To protect the rights and safety of our users",
          t("priv_s3_li4") || "In connection with a merger or acquisition (with notice)",
        ]} />
        <InfoBox icon="bi-lock">{t("priv_s3_info") || "We never sell your personal information to third parties or advertisers."}</InfoBox>
      </Section>

      <Section id="cookies" number="04" title={t("priv_s4_title") || "Cookies & Tracking"}>
        <P>{t("priv_s4_p1") || <>We use cookies and similar technologies to enhance your experience. See our <Link to="/cookies" className="lp-link">{t("cookie_policy") || "Cookie Policy"}</Link> for full details.</>}</P>
        <Ul items={[
          t("priv_s4_li1") || "Essential cookies: required for the site to function",
          t("priv_s4_li2") || "Analytics cookies: help us understand how you use our site",
          t("priv_s4_li3") || "Preference cookies: remember your language and display settings",
        ]} />
      </Section>

      <Section id="rights" number="05" title={t("priv_s5_title") || "Your Rights"}>
        <P>{t("priv_s5_p1") || "Depending on your location, you may have the following rights regarding your personal data."}</P>
        <Ul items={[
          t("priv_s5_li1") || "Access the personal data we hold about you",
          t("priv_s5_li2") || "Correct inaccurate or incomplete information",
          t("priv_s5_li3") || "Request deletion of your account and data",
          t("priv_s5_li4") || "Object to certain types of processing",
          t("priv_s5_li5") || "Export your data in a portable format",
          t("priv_s5_li6") || "Withdraw consent at any time",
        ]} />
      </Section>

      <Section id="security" number="06" title={t("priv_s6_title") || "Security"}>
        <P>{t("priv_s6_p1") || "We implement industry-standard security measures to protect your data, including encryption in transit and at rest, regular security audits, and access controls."}</P>
      </Section>

      <Section id="contact" number="07" title={t("priv_s7_title") || "Contact Us"}>
        <P>{t("priv_s7_p1") || "If you have questions about this privacy policy or your personal data, contact our privacy team."}</P>
        <div className="lp-contact-card">
          <i className="bi bi-envelope-at lp-contact-card__icon" />
          <div>
            <div className="lp-contact-card__label">{t("privacy_team") || "Privacy Team"}</div>
            <a href="mailto:newsteam@24newsread.com" className="lp-link">newsteam@24newsread.com</a>
          </div>
        </div>
      </Section>
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   TERMS PAGE
═══════════════════════════════════════════════════════════════════════════════ */
export function TermsPage() {
  const { t } = useTranslation();

  const sections = [
    { id: "acceptance",   label: t("terms_s1_title") || "Acceptance of Terms" },
    { id: "account",      label: t("terms_s2_title") || "Your Account" },
    { id: "content",      label: t("terms_s3_title") || "User Content" },
    { id: "prohibited",   label: t("terms_s4_title") || "Prohibited Conduct" },
    { id: "intellectual", label: t("terms_s5_title") || "Intellectual Property" },
    { id: "disclaimers",  label: t("terms_s6_title") || "Disclaimers" },
    { id: "termination",  label: t("terms_s7_title") || "Termination" },
    { id: "governing",    label: t("terms_s8_title") || "Governing Law" },
  ];

  return (
    <LegalShell
      icon={<i className="bi bi-file-earmark-text" />}
      badge={t("legal_badge") || "Legal Document"}
      title={t("terms_title") || "Terms of Service"}
      subtitle={t("terms_subtitle") || "Please read these terms carefully before using 24News."}
      updated={t("terms_updated") || "March 15, 2026"}
      sections={sections}
    >
      <InfoBox icon="bi-exclamation-circle">
        {t("terms_intro") || "By accessing or using 24News, you agree to be bound by these Terms of Service and our Privacy Policy."}
      </InfoBox>

      <Section id="acceptance" number="01" title={t("terms_s1_title") || "Acceptance of Terms"}>
        <P>{t("terms_s1_p1") || "By creating an account or using our services, you confirm that you are at least 13 years old and agree to these terms. If you are under 18, you confirm you have parental consent."}</P>
      </Section>

      <Section id="account" number="02" title={t("terms_s2_title") || "Your Account"}>
        <P>{t("terms_s2_p1") || "You are responsible for maintaining the security of your account and all activity that occurs under it."}</P>
        <Ul items={[
          t("terms_s2_li1") || "Use a strong, unique password for your account",
          t("terms_s2_li2") || "Do not share your credentials with others",
          t("terms_s2_li3") || "Notify us immediately of any unauthorized access",
          t("terms_s2_li4") || "Provide accurate and current information",
          t("terms_s2_li5") || "One account per person — multiple accounts are not permitted",
        ]} />
      </Section>

      <Section id="content" number="03" title={t("terms_s3_title") || "User Content"}>
        <P>{t("terms_s3_p1") || "When you post comments or other content, you grant 24News a non-exclusive, royalty-free license to display and distribute that content on our platform."}</P>
        <P>{t("terms_s3_p2") || "You retain ownership of your content and are responsible for ensuring it does not violate any laws or third-party rights."}</P>
      </Section>

      <Section id="prohibited" number="04" title={t("terms_s4_title") || "Prohibited Conduct"}>
        <P>{t("terms_s4_p1") || "The following are strictly prohibited on our platform."}</P>
        <Ul items={[
          t("terms_s4_li1") || "Posting false, misleading, or defamatory content",
          t("terms_s4_li2") || "Harassment, hate speech, or threats of violence",
          t("terms_s4_li3") || "Spam, phishing, or malicious code",
          t("terms_s4_li4") || "Impersonating others or misrepresenting affiliation",
          t("terms_s4_li5") || "Scraping or automated data collection without permission",
          t("terms_s4_li6") || "Circumventing security or access controls",
        ]} />
        <InfoBox icon="bi-exclamation-triangle">
          {t("terms_s4_info") || "Violations may result in content removal, account suspension, or permanent ban."}
        </InfoBox>
      </Section>

      <Section id="intellectual" number="05" title={t("terms_s5_title") || "Intellectual Property"}>
        <P>{t("terms_s5_p1") || "All editorial content, logos, trademarks, and design elements on 24News are owned by or licensed to us. You may not reproduce, distribute, or create derivative works without explicit permission."}</P>
      </Section>

      <Section id="disclaimers" number="06" title={t("terms_s6_title") || "Disclaimers"}>
        <P>{t("terms_s6_p1") || "24News provides news and information \"as is\" without warranties of any kind. We do not guarantee the accuracy, completeness, or timeliness of any content."}</P>
      </Section>

      <Section id="termination" number="07" title={t("terms_s7_title") || "Termination"}>
        <P>{t("terms_s7_p1") || "We reserve the right to suspend or terminate your account at any time for violations of these terms. You may delete your account at any time from your profile settings."}</P>
      </Section>

      <Section id="governing" number="08" title={t("terms_s8_title") || "Governing Law"}>
        <P>{t("terms_s8_p1") || "These terms are governed by applicable law. Any disputes shall be resolved through binding arbitration, except where prohibited by law."}</P>
      </Section>
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   COOKIES PAGE  — preferences saved to backend
═══════════════════════════════════════════════════════════════════════════════ */
function getCookieTypeData(t) {
  return [
    {
      key: "essential",
      icon: "bi-shield-fill-check",
      name: t("cookie_type_essential") || "Essential",
      color: "#22c55e",
      desc: t("cookie_type_essential_desc") || "Required for the website to function. Cannot be disabled.",
      examples: [
        t("cookie_ex_session")  || "Session authentication",
        t("cookie_ex_security") || "Security tokens",
        t("cookie_ex_lang")     || "Language preference",
      ],
      required: true,
    },
    {
      key: "analytics",
      icon: "bi-graph-up",
      name: t("cookie_type_analytics") || "Analytics",
      color: "#3b82f6",
      desc: t("cookie_type_analytics_desc") || "Help us understand how visitors interact with our website.",
      examples: [
        t("cookie_ex_pageviews") || "Page views",
        t("cookie_ex_time")      || "Time on site",
        t("cookie_ex_nav")       || "Navigation paths",
      ],
      required: false,
    },
    {
      key: "preferences",
      icon: "bi-sliders",
      name: t("cookie_type_preferences") || "Preferences",
      color: "#f59e0b",
      desc: t("cookie_type_preferences_desc") || "Remember your settings to personalize your experience.",
      examples: [
        t("cookie_ex_darkmode") || "Dark mode",
        t("cookie_ex_fontsize") || "Font size",
        t("cookie_ex_filters")  || "Saved filters",
      ],
      required: false,
    },
    {
      key: "marketing",
      icon: "bi-megaphone",
      name: t("cookie_type_marketing") || "Marketing",
      color: "#e63311",
      desc: t("cookie_type_marketing_desc") || "Used to deliver relevant content and measure effectiveness.",
      examples: [
        t("cookie_ex_reco")     || "Content recommendations",
        t("cookie_ex_social")   || "Social sharing",
        t("cookie_ex_campaign") || "Campaign tracking",
      ],
      required: false,
    },
  ];
}

function getToken() {
  try {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || null;
  } catch { return null; }
}
function authHeaders() {
  const token = getToken();
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
  return h;
}

export function CookiesPage() {
  const { t } = useTranslation();
  const COOKIE_TYPES = getCookieTypeData(t);

  const [prefs,      setPrefs]      = useState({ analytics: true, preferences: true, marketing: false });
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [loadStatus, setLoadStatus] = useState("idle"); // idle | loading | loaded

  // ── Load saved prefs from backend on mount ────────────────────────────────
 useEffect(() => {
  setLoadStatus("loading");
  axios.get(`${API_BASE}/cookie-preferences/`, { headers: authHeaders() })
    .then(res => {
      if (res.data) {
        const loaded = {
          analytics:   !!res.data.analytics,
          preferences: !!res.data.preferences,
          marketing:   !!res.data.marketing,
        };
        setPrefs(loaded);

        // ← زيد هاد السطرين
        updateConsent(loaded.analytics);
        updateAdsConsent(loaded.marketing);
      }
      setLoadStatus("loaded");
    })
    .catch(() => setLoadStatus("loaded"));
}, []);

  // ── Save prefs to backend ─────────────────────────────────────────────────
const savePrefs = async () => {
  setSaveStatus("saving");
  try {
    await axios.post(
      `${API_BASE}/cookie-preferences/`,
      { analytics: prefs.analytics, preferences: prefs.preferences, marketing: prefs.marketing },
      { headers: authHeaders() }
    );
    localStorage.setItem("cookie_prefs", JSON.stringify(prefs));
    window.dispatchEvent(new Event("cookie-prefs-changed"));

    // ← زيد هاد السطرين
    updateConsent(prefs.analytics);
    updateAdsConsent(prefs.marketing);

    setSaveStatus("saved");
    setTimeout(() => setSaveStatus("idle"), 2500);
  } catch {
    localStorage.setItem("cookie_prefs", JSON.stringify(prefs));
    window.dispatchEvent(new Event("cookie-prefs-changed"));

    // ← وهنا أيضا، حتى إلا فشل الـ backend، دير تحديث محلي
    updateConsent(prefs.analytics);
    updateAdsConsent(prefs.marketing);

    setSaveStatus("saved");
    setTimeout(() => setSaveStatus("idle"), 2500);
  }
};

  const sections = [
    { id: "what",    label: t("cookie_s1_title") || "What Are Cookies" },
    { id: "types",   label: t("cookie_s2_title") || "Types We Use" },
    { id: "manage",  label: t("cookie_s3_title") || "Manage Preferences" },
    { id: "third",   label: t("cookie_s4_title") || "Third-Party Cookies" },
    { id: "contact", label: t("cookie_s5_title") || "Contact" },
  ];

  return (
    <LegalShell
      icon={<i className="bi bi-cookie" />}
      badge={t("legal_badge") || "Legal Document"}
      title={t("cookies_title") || "Cookie Policy"}
      subtitle={t("cookies_subtitle") || "We use cookies to improve your experience and analyze site performance."}
      updated={t("cookies_updated") || "March 15, 2026"}
      sections={sections}
    >
      <Section id="what" number="01" title={t("cookie_s1_title") || "What Are Cookies?"}>
        <P>{t("cookie_s1_p1") || "Cookies are small text files stored on your device when you visit a website. They help remember your preferences and improve your browsing experience."}</P>
        <InfoBox icon="bi-info-circle">
          {t("cookie_s1_info") || "Cookies do not contain personally identifiable information on their own, but may be combined with other data we hold about you."}
        </InfoBox>
      </Section>

      <Section id="types" number="02" title={t("cookie_s2_title") || "Types of Cookies We Use"}>
        <div className="lp-cookie-grid">
          {COOKIE_TYPES.map((ct) => (
            <div key={ct.key} className="lp-cookie-card">
              <div className="lp-cookie-card__head">
                <div className="lp-cookie-card__icon" style={{ background: ct.color + "20", color: ct.color }}>
                  <i className={`bi ${ct.icon}`} />
                </div>
                <div>
                  <div className="lp-cookie-card__name">{ct.name}</div>
                  {ct.required && <span className="lp-cookie-card__req">{t("cookie_required") || "Required"}</span>}
                </div>
              </div>
              <p className="lp-cookie-card__desc">{ct.desc}</p>
              <ul className="lp-cookie-card__list">
                {ct.examples.map((ex) => (
                  <li key={ex}><i className="bi bi-dot" />{ex}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section id="manage" number="03" title={t("cookie_s3_title") || "Manage Your Preferences"}>
        <P>{t("cookie_s3_p1") || "You can control which optional cookies we use. Essential cookies cannot be disabled as they are required for the site to function."}</P>

        {loadStatus === "loading" && (
          <div className="lp-loading-bar">
            <div className="lp-loading-bar__inner" />
          </div>
        )}

        <div className="lp-prefs">
          {COOKIE_TYPES.map((ct) => (
            <div key={ct.key} className="lp-pref-row">
              <div className="lp-pref-row__info">
                <div className="lp-pref-row__name" style={{ color: ct.color }}>
                  <i className={`bi ${ct.icon}`} /> {ct.name}
                </div>
                <div className="lp-pref-row__desc">{ct.desc}</div>
              </div>
              <label className={`lp-toggle${ct.required ? " lp-toggle--locked" : ""}`}>
                <input
                  type="checkbox"
                  checked={ct.required || prefs[ct.key]}
                  disabled={ct.required}
                  onChange={(e) =>
                    !ct.required && setPrefs(p => ({ ...p, [ct.key]: e.target.checked }))
                  }
                />
                <span className="lp-toggle__slider" />
                <span className="lp-sr-only">{ct.name}</span>
              </label>
            </div>
          ))}

          <div className="lp-prefs__footer">
            <div className="lp-prefs__footer-left">
              {saveStatus === "saved" && (
                <span className="lp-prefs__saved-msg">
                  <i className="bi bi-check-circle" /> {t("cookie_prefs_saved") || "Preferences saved!"}
                </span>
              )}
              {saveStatus === "error" && (
                <span className="lp-prefs__error-msg">
                  <i className="bi bi-exclamation-circle" /> {t("cookie_prefs_error") || "Could not save. Saved locally."}
                </span>
              )}
            </div>
            <button
              className={`lp-save-btn${saveStatus === "saved" ? " lp-save-btn--ok" : ""}`}
              onClick={savePrefs}
              disabled={saveStatus === "saving"}
            >
              {saveStatus === "saving" ? (
                <><div className="lp-btn-spinner" /> {t("saving") || "Saving…"}</>
              ) : saveStatus === "saved" ? (
                <><i className="bi bi-check-lg" /> {t("saved") || "Saved!"}</>
              ) : (
                t("save_preferences") || "Save Preferences"
              )}
            </button>
          </div>
        </div>
      </Section>

      <Section id="third" number="04" title={t("cookie_s4_title") || "Third-Party Cookies"}>
        <P>{t("cookie_s4_p1") || "Some features use cookies from trusted third-party services."}</P>
        <Ul items={[
          t("cookie_s4_li1") || "Analytics: aggregated usage statistics",
          t("cookie_s4_li2") || "Social sharing: share buttons from social networks",
          t("cookie_s4_li3") || "Maps: geographic content embeds",
        ]} />
        <P>{t("cookie_s4_p2") || "These providers have their own privacy policies which we encourage you to review."}</P>
      </Section>

      <Section id="contact" number="05" title={t("cookie_s5_title") || "Questions?"}>
        <P>{t("cookie_s5_p1") || "For questions about our use of cookies, please contact our privacy team."}</P>
        <div className="lp-contact-card">
          <i className="bi bi-envelope-at lp-contact-card__icon" />
          <div>
            <div className="lp-contact-card__label">{t("privacy_team") || "Privacy Team"}</div>
            <a href="mailto:newsteam@24newsread.com" className="lp-link">newsteam@24newsread.com</a>
          </div>
        </div>
      </Section>
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SITEMAP PAGE
═══════════════════════════════════════════════════════════════════════════════ */
export function SitemapPage() {
  const { t } = useTranslation();
  const [query,    setQuery]    = useState("");
  const inputRef = useRef(null);

  const SITEMAP_DATA = [
    {
      icon: "bi-house", color: "#e63311",
      title: t("sitemap_main") || "Main",
      links: [
        { label: t("nav_home")    || "Home",        to: "/" },
        { label: t("nav_latest")  || "Latest News", to: "/latest" },
        { label: t("nav_breaking")|| "Breaking",    to: "/breaking" },
        { label: t("nav_trending")|| "Trending",    to: "/trending" },
      ],
    },
    {
      icon: "bi-grid-3x3-gap", color: "#3b82f6",
      title: t("sitemap_subjects") || "Subjects",
      links: [
        { label: t("all_subjects")       || "All Subjects",  to: "/subjects" },
        { label: t("subj_technology")    || "Technology",    to: "/subject/technology" },
        { label: t("subj_politics")      || "Politics",      to: "/subject/politics" },
        { label: t("subj_business")      || "Business",      to: "/subject/business" },
        { label: t("subj_science")       || "Science",       to: "/subject/science" },
        { label: t("subj_sports")        || "Sports",        to: "/subject/sports" },
      ],
    },
    {
      icon: "bi-tag", color: "#22c55e",
      title: t("sitemap_categories") || "Categories",
      links: [
        { label: t("cat_world")       || "World",         to: "/category/world" },
        { label: t("cat_middleeast")  || "Middle East",   to: "/category/middle-east" },
        { label: t("cat_africa")      || "Africa",        to: "/category/africa" },
        { label: t("cat_economy")     || "Economy",       to: "/category/economy" },
        { label: t("cat_health")      || "Health",        to: "/category/health" },
        { label: t("cat_culture")     || "Culture",       to: "/category/culture" },
      ],
    },
    {
      icon: "bi-person-circle", color: "#f59e0b",
      title: t("sitemap_account") || "Account",
      links: [
        { label: t("login")           || "Login",          to: "/login" },
        { label: t("register")        || "Register",       to: "/register" },
        { label: t("profile")         || "Profile",        to: "/profile" },
        { label: t("saved_articles")  || "Saved Articles", to: "/profile?tab=saved" },
      ],
    },
    {
      icon: "bi-info-circle", color: "#8b5cf6",
      title: t("sitemap_about") || "About",
      links: [
        { label: t("about_us")    || "About Us",   to: "/about" },
        { label: t("contact")     || "Contact",    to: "/contact" },
        { label: t("advertise")   || "Advertise",  to: "/advertise" },
        { label: t("careers")     || "Careers",    to: "/careers" },
      ],
    },
    {
      icon: "bi-shield-lock", color: "#6b7280",
      title: t("sitemap_legal") || "Legal",
      links: [
        { label: t("privacy_title") || "Privacy Policy",   to: "/privacy" },
        { label: t("terms_title")   || "Terms of Service", to: "/terms" },
        { label: t("cookies_title") || "Cookie Policy",    to: "/cookies" },
        { label: t("sitemap_title") || "Sitemap",          to: "/sitemap" },
      ],
    },
  ];

  const filtered = query.trim()
    ? SITEMAP_DATA.map(g => ({
        ...g,
        links: g.links.filter(l => l.label.toLowerCase().includes(query.toLowerCase())),
      })).filter(g => g.links.length > 0)
    : SITEMAP_DATA;

  const totalPages = SITEMAP_DATA.reduce((a, g) => a + g.links.length, 0);

  return (
    <LegalShell
      icon={<i className="bi bi-diagram-3" />}
      badge={t("navigation_badge") || "Navigation"}
      title={t("sitemap_title") || "Sitemap"}
      subtitle={t("sitemap_subtitle") || "A complete overview of all pages and sections on 24News."}
    >
      <div className="lp-sm-search">
        <i className="bi bi-search lp-sm-search__icon" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          className="lp-sm-search__input"
          placeholder={t("sitemap_search_placeholder") || "Search pages…"}
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label={t("sitemap_search_label") || "Search sitemap"}
        />
        {query && (
          <button className="lp-sm-search__clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }}>
            <i className="bi bi-x-lg" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="lp-sm-empty">
          <i className="bi bi-search" />
          <p>{t("sitemap_no_results") || <>No pages found for "<strong>{query}</strong>"</>}</p>
        </div>
      ) : (
        <div className="lp-sm-grid">
          {filtered.map(group => (
            <div key={group.title} className="lp-sm-group">
              <div className="lp-sm-group__head">
                <div className="lp-sm-group__icon" style={{ color: group.color, background: group.color + "18" }}>
                  <i className={`bi ${group.icon}`} />
                </div>
                <h2 className="lp-sm-group__title">{group.title}</h2>
                <span className="lp-sm-group__count">{group.links.length}</span>
              </div>
              <ul className="lp-sm-list">
                {group.links.map(link => (
                  <li key={link.to}>
                    <Link to={link.to} className="lp-sm-link">
                      <i className="bi bi-arrow-right lp-sm-link__arrow" aria-hidden />
                      <span>{link.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="lp-sm-stats">
        {[
          { n: totalPages,         l: t("stat_total_pages") || "Total pages" },
          { n: SITEMAP_DATA.length, l: t("stat_sections")   || "Sections" },
          { n: 4,                  l: t("stat_languages")   || "Languages" },
        ].map(({ n, l }) => (
          <div key={l} className="lp-sm-stat">
            <span className="lp-sm-stat__n">{n}</span>
            <span className="lp-sm-stat__l">{l}</span>
          </div>
        ))}
      </div>
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   CSS  (identical to original — only new utility classes added at bottom)
═══════════════════════════════════════════════════════════════════════════════ */
const LEGAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=Noto+Sans+Arabic:wght@300;400;500;600;700&display=swap');

:root {
  --lp-red:    #e63311;
  --lp-red-dk: #c4280d;
  --lp-ink:    #111;
  --lp-ink2:   #6b7280;
  --lp-bg:     #fff;
  --lp-bg2:    #f7f7f7;
  --lp-border: rgba(0,0,0,.08);
  --lp-sh:     0 1px 4px rgba(0,0,0,.06);
  --lp-sh-md:  0 8px 24px rgba(0,0,0,.10);
  --lp-r:      12px;
  --lp-font:   'Sora','Helvetica Neue',sans-serif;
  --lp-t:      0.18s cubic-bezier(.4,0,.2,1);
}
@media (prefers-color-scheme: dark) {
  :root {
    --lp-ink:    #f1f1f1;
    --lp-ink2:   #9ca3af;
    --lp-bg:     #111;
    --lp-bg2:    #1a1a1a;
    --lp-border: rgba(255,255,255,.09);
  }
}

.lp-root, .lp-root * { box-sizing: border-box; }
.lp-root { font-family:var(--lp-font);color:var(--lp-ink);background:var(--lp-bg2);min-height:100vh; }
.lp-sr-only { position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0; }
.lp-container { max-width:1160px;margin:0 auto;padding:0 clamp(16px,4vw,40px); }

.lp-progress-track { position:fixed;top:0;left:0;right:0;height:3px;background:var(--lp-border);z-index:9999; }
.lp-progress-bar   { height:100%;background:var(--lp-red);transition:width .1s linear; }

.lp-hero { position:relative;background:#0d0d0d;overflow:hidden;padding:clamp(52px,9vw,96px) 0 clamp(40px,7vw,72px); }
.lp-hero__bg { position:absolute;inset:0;background:radial-gradient(ellipse at 70% 50%,rgba(230,51,17,.22) 0%,transparent 65%),radial-gradient(ellipse at 10% 80%,rgba(230,51,17,.1) 0%,transparent 55%); }
.lp-hero__noise { position:absolute;inset:0;pointer-events:none;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.035'/%3E%3C/svg%3E"); }
.lp-hero__inner  { position:relative;z-index:1;display:flex;align-items:flex-start;gap:24px;flex-wrap:wrap; }
.lp-hero__icon   { width:64px;height:64px;border-radius:16px;background:rgba(230,51,17,.18);border:1.5px solid rgba(230,51,17,.35);display:flex;align-items:center;justify-content:center;font-size:1.75rem;color:var(--lp-red);flex-shrink:0;margin-top:4px; }
.lp-hero__badge  { display:inline-flex;align-items:center;font-size:.62rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--lp-red);margin-bottom:10px;padding:3px 10px;background:rgba(230,51,17,.12);border:1px solid rgba(230,51,17,.25);border-radius:100px; }
.lp-hero__title  { font-size:clamp(1.75rem,5vw,3.25rem);font-weight:800;letter-spacing:-.03em;color:#fff;margin:0 0 10px;line-height:1.1; }
.lp-hero__sub    { font-size:clamp(.875rem,1.8vw,1.05rem);color:rgba(255,255,255,.5);margin:0 0 16px;max-width:560px;line-height:1.65;font-weight:300; }
.lp-hero__meta   { display:flex;align-items:center;gap:7px;font-size:.75rem;color:rgba(255,255,255,.35);font-weight:500; }
.lp-hero__meta .bi { color:var(--lp-red); }

.lp-breadcrumb        { background:var(--lp-bg);border-bottom:1px solid var(--lp-border);padding:11px 0; }
.lp-breadcrumb__inner { display:flex;align-items:center;gap:8px;font-size:.78rem;font-weight:500;color:var(--lp-ink2); }
.lp-breadcrumb__link  { display:flex;align-items:center;gap:5px;color:var(--lp-ink2);text-decoration:none;transition:color var(--lp-t); }
.lp-breadcrumb__link:hover { color:var(--lp-red); }
.lp-breadcrumb__sep   { color:var(--lp-border); }
.lp-breadcrumb__cur   { color:var(--lp-ink); }

.lp-body { display:grid;grid-template-columns:240px 1fr;gap:clamp(24px,4vw,48px);align-items:start;padding-top:40px;padding-bottom:80px; }
@media (max-width:900px) { .lp-body { grid-template-columns:1fr; } }

.lp-toc { position:sticky;top:80px;background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);overflow:hidden;box-shadow:var(--lp-sh); }
@media (max-width:900px) { .lp-toc { display:none; } }
.lp-toc__head  { display:flex;align-items:center;gap:9px;padding:14px 16px;border-bottom:1px solid var(--lp-border); }
.lp-toc__bar   { width:3px;height:16px;background:var(--lp-red);border-radius:2px;flex-shrink:0; }
.lp-toc__title { font-size:.68rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--lp-ink2);margin:0; }
.lp-toc__list  { list-style:none;margin:0;padding:8px 0; }
.lp-toc__item  { display:flex;align-items:center;gap:10px;width:100%;padding:9px 16px;background:none;border:none;font-family:var(--lp-font);font-size:.78rem;font-weight:500;color:var(--lp-ink2);cursor:pointer;text-align:left;transition:color var(--lp-t),background var(--lp-t); }
.lp-toc__item:hover      { color:var(--lp-red);background:rgba(230,51,17,.05); }
.lp-toc__item--on        { color:var(--lp-red);font-weight:700; }
.lp-toc__num             { font-size:.62rem;font-weight:700;color:var(--lp-red);min-width:22px;opacity:.6; }
.lp-toc__item--on .lp-toc__num { opacity:1; }

.lp-article { min-width:0; }

.lp-section { margin-bottom:48px;scroll-margin-top:100px; }
.lp-section__head  { display:flex;align-items:baseline;gap:14px;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid var(--lp-border);position:relative; }
.lp-section__head::after { content:'';position:absolute;bottom:-2px;left:0;width:48px;height:2px;background:var(--lp-red);border-radius:2px; }
.lp-section__num   { font-size:.65rem;font-weight:800;letter-spacing:.12em;color:var(--lp-red);opacity:.7;min-width:28px; }
.lp-section__title { font-size:clamp(1.1rem,2.5vw,1.4rem);font-weight:800;letter-spacing:-.02em;color:var(--lp-ink);margin:0; }

.lp-p   { font-size:.9375rem;line-height:1.8;color:var(--lp-ink2);margin:0 0 14px; }
.lp-ul  { list-style:none;margin:0 0 16px;padding:0;display:flex;flex-direction:column;gap:8px; }
.lp-li  { display:flex;align-items:flex-start;gap:10px;font-size:.875rem;line-height:1.6;color:var(--lp-ink2); }
.lp-li__dot { width:7px;height:7px;border-radius:50%;background:var(--lp-red);flex-shrink:0;margin-top:6px; }
.lp-link    { color:var(--lp-red);text-decoration:underline;text-decoration-color:rgba(230,51,17,.3);text-underline-offset:3px;transition:color var(--lp-t); }
.lp-link:hover { color:var(--lp-red-dk); }

.lp-info { display:flex;gap:13px;padding:16px 18px;background:rgba(230,51,17,.06);border:1px solid rgba(230,51,17,.18);border-radius:10px;margin:0 0 20px; }
.lp-info__icon { font-size:1.1rem;color:var(--lp-red);flex-shrink:0;margin-top:1px; }
.lp-info > div { font-size:.875rem;line-height:1.65;color:var(--lp-ink2); }
.lp-info strong { color:var(--lp-ink); }

.lp-contact-card       { display:flex;align-items:center;gap:16px;padding:18px 20px;background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);box-shadow:var(--lp-sh); }
.lp-contact-card__icon { font-size:1.5rem;color:var(--lp-red); }
.lp-contact-card__label{ font-size:.7rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--lp-ink2);margin-bottom:3px; }

.lp-cookie-grid { display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:14px;margin-bottom:24px; }
.lp-cookie-card { background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);padding:18px;transition:box-shadow var(--lp-t),border-color var(--lp-t); }
.lp-cookie-card:hover { box-shadow:var(--lp-sh-md); }
.lp-cookie-card__head  { display:flex;align-items:center;gap:11px;margin-bottom:10px; }
.lp-cookie-card__icon  { width:38px;height:38px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:1.1rem;flex-shrink:0; }
.lp-cookie-card__name  { font-size:.9rem;font-weight:700;color:var(--lp-ink); }
.lp-cookie-card__req   { display:inline-block;font-size:.55rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;background:rgba(34,197,94,.12);color:#16a34a;padding:2px 7px;border-radius:100px;margin-top:2px; }
.lp-cookie-card__desc  { font-size:.78rem;color:var(--lp-ink2);line-height:1.55;margin:0 0 10px; }
.lp-cookie-card__list  { list-style:none;margin:0;padding:0; }
.lp-cookie-card__list li { font-size:.74rem;color:var(--lp-ink2);display:flex;align-items:center;gap:2px;padding:2px 0; }
.lp-cookie-card__list .bi { color:var(--lp-red); }

.lp-prefs     { background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);overflow:hidden; }
.lp-pref-row  { display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 20px;border-bottom:1px solid var(--lp-border); }
.lp-pref-row__info { flex:1;min-width:0; }
.lp-pref-row__name { font-size:.85rem;font-weight:700;display:flex;align-items:center;gap:7px;margin-bottom:3px; }
.lp-pref-row__desc { font-size:.75rem;color:var(--lp-ink2); }

.lp-toggle       { position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0; }
.lp-toggle input { opacity:0;width:0;height:0; }
.lp-toggle__slider { position:absolute;inset:0;background:#d1d5db;border-radius:100px;cursor:pointer;transition:background .2s; }
.lp-toggle input:checked + .lp-toggle__slider { background:var(--lp-red); }
.lp-toggle__slider::before { content:'';position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;left:3px;top:3px;transition:transform .2s;box-shadow:0 1px 3px rgba(0,0,0,.2); }
.lp-toggle input:checked + .lp-toggle__slider::before { transform:translateX(20px); }
.lp-toggle--locked .lp-toggle__slider { background:#22c55e;cursor:not-allowed; }
.lp-toggle--locked .lp-toggle__slider::before { transform:translateX(20px); }

/* Updated prefs footer — save btn + status msg */
.lp-prefs__footer       { display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 20px;border-top:1px solid var(--lp-border);flex-wrap:wrap; }
.lp-prefs__footer-left  { font-size:.8rem;display:flex;align-items:center;gap:6px; }
.lp-prefs__saved-msg    { color:#16a34a;font-weight:600;display:flex;align-items:center;gap:5px;animation:lp-fade-in .3s ease; }
.lp-prefs__error-msg    { color:#dc2626;font-weight:600;display:flex;align-items:center;gap:5px;animation:lp-fade-in .3s ease; }

.lp-save-btn {
  display:inline-flex;align-items:center;justify-content:center;gap:7px;
  padding:10px 20px;border-radius:9px;border:none;cursor:pointer;
  background:var(--lp-red);color:#fff;font-family:var(--lp-font);
  font-size:.825rem;font-weight:700;letter-spacing:.03em;
  transition:background var(--lp-t),transform .1s;min-width:140px;
}
.lp-save-btn:hover:not(:disabled) { background:var(--lp-red-dk); }
.lp-save-btn:active:not(:disabled){ transform:scale(.98); }
.lp-save-btn:disabled { opacity:.65;cursor:not-allowed; }
.lp-save-btn--ok      { background:#22c55e; }
.lp-save-btn--ok:hover { background:#16a34a; }

.lp-btn-spinner { width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:lp-spin .7s linear infinite; }

/* Loading bar */
.lp-loading-bar       { height:3px;background:var(--lp-border);border-radius:2px;overflow:hidden;margin-bottom:16px; }
.lp-loading-bar__inner{ height:100%;background:var(--lp-red);animation:lp-loading 1.2s ease-in-out infinite; }

.lp-sm-search { position:relative;margin-bottom:32px; }
.lp-sm-search__icon  { position:absolute;left:14px;top:50%;transform:translateY(-50%);color:var(--lp-ink2);font-size:.9rem;pointer-events:none; }
.lp-sm-search__input { width:100%;padding:13px 44px;background:var(--lp-bg);border:1.5px solid var(--lp-border);border-radius:var(--lp-r);font-size:.9rem;font-family:var(--lp-font);color:var(--lp-ink);outline:none;transition:border-color var(--lp-t),box-shadow var(--lp-t); }
.lp-sm-search__input:focus { border-color:var(--lp-red);box-shadow:0 0 0 3px rgba(230,51,17,.1); }
.lp-sm-search__input::placeholder { color:var(--lp-ink2); }
.lp-sm-search__input::-webkit-search-cancel-button { display:none; }
.lp-sm-search__clear { position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--lp-ink2);font-size:.75rem;width:28px;height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;transition:background var(--lp-t),color var(--lp-t); }
.lp-sm-search__clear:hover { background:var(--lp-bg2);color:var(--lp-ink); }

.lp-sm-empty { display:flex;flex-direction:column;align-items:center;gap:12px;padding:60px 20px;color:var(--lp-ink2);font-size:.9rem; }
.lp-sm-empty .bi { font-size:2rem;opacity:.3; }

.lp-sm-grid  { display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px;margin-bottom:40px; }
.lp-sm-group { background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);overflow:hidden;transition:box-shadow var(--lp-t); }
.lp-sm-group:hover { box-shadow:var(--lp-sh-md); }
.lp-sm-group__head  { display:flex;align-items:center;gap:11px;padding:14px 18px;border-bottom:1px solid var(--lp-border); }
.lp-sm-group__icon  { width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0; }
.lp-sm-group__title { font-size:.875rem;font-weight:700;color:var(--lp-ink);flex:1; }
.lp-sm-group__count { font-size:.62rem;font-weight:700;background:var(--lp-bg2);border:1px solid var(--lp-border);padding:2px 8px;border-radius:100px;color:var(--lp-ink2); }
.lp-sm-list  { list-style:none;margin:0;padding:8px 0; }
.lp-sm-link  { display:flex;align-items:center;gap:9px;padding:9px 18px;color:var(--lp-ink2);text-decoration:none;font-size:.825rem;font-weight:500;transition:color var(--lp-t),background var(--lp-t); }
.lp-sm-link:hover { color:var(--lp-red);background:rgba(230,51,17,.05); }
.lp-sm-link__arrow { font-size:.75rem;color:var(--lp-border);transition:color var(--lp-t),transform var(--lp-t); }
.lp-sm-link:hover .lp-sm-link__arrow { color:var(--lp-red);transform:translateX(3px); }

.lp-sm-stats { display:flex;gap:0;background:var(--lp-bg);border:1px solid var(--lp-border);border-radius:var(--lp-r);overflow:hidden; }
.lp-sm-stat  { flex:1;display:flex;flex-direction:column;align-items:center;padding:20px;border-right:1px solid var(--lp-border); }
.lp-sm-stat:last-child { border-right:none; }
.lp-sm-stat__n { font-size:1.75rem;font-weight:800;letter-spacing:-.03em;color:var(--lp-red);line-height:1; }
.lp-sm-stat__l { font-size:.65rem;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--lp-ink2);margin-top:4px; }

.lp-btt { position:fixed;bottom:28px;right:28px;width:42px;height:42px;border-radius:10px;background:var(--lp-red);color:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:.9rem;box-shadow:0 4px 16px rgba(230,51,17,.4);opacity:0;transform:translateY(12px);pointer-events:none;transition:opacity var(--lp-t),transform var(--lp-t),background var(--lp-t);z-index:900; }
.lp-btt--on { opacity:1;transform:none;pointer-events:all; }
.lp-btt:hover { background:var(--lp-red-dk); }

[dir="rtl"] .lp-sm-link__arrow { transform:scaleX(-1); }
[dir="rtl"] .lp-sm-link:hover .lp-sm-link__arrow { transform:scaleX(-1) translateX(-3px); }
[dir="rtl"] .lp-toc__item { text-align:right; }
[dir="rtl"] .lp-hero__inner { direction:rtl; }
[dir="rtl"] .lp-sm-search__icon { left:auto;right:14px; }
[dir="rtl"] .lp-sm-search__input { padding:13px 44px 13px 44px; }
[dir="rtl"] .lp-btt { right:auto;left:28px; }
[dir="rtl"] .lp-toggle__slider::before { left:auto;right:3px; }
[dir="rtl"] .lp-toggle input:checked + .lp-toggle__slider::before { transform:translateX(-20px); }
[dir="rtl"] .lp-section__head::after { left:auto;right:0; }

@keyframes lp-fade-in { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
@keyframes lp-spin    { to{transform:rotate(360deg)} }
@keyframes lp-loading { 0%{width:0%;margin-left:0} 50%{width:60%;margin-left:20%} 100%{width:0%;margin-left:100%} }

@media (max-width:640px) {
  .lp-cookie-grid { grid-template-columns:1fr; }
  .lp-sm-grid { grid-template-columns:1fr 1fr; }
  .lp-sm-stats { flex-direction:column; }
  .lp-sm-stat  { border-right:none;border-bottom:1px solid var(--lp-border); }
  .lp-sm-stat:last-child { border-bottom:none; }
  .lp-prefs__footer { flex-direction:column;align-items:stretch; }
  .lp-save-btn { width:100%; }
}
@media (max-width:400px) {
  .lp-sm-grid { grid-template-columns:1fr; }
}
`;
